/**
 * MultilingualRedirector — Vilax shadow.
 *
 * Extends Volto's MultilingualRedirector to also redirect bare public
 * non-content routes into the language tree.  Volto already supports
 * language-prefixed variants (/{lang}/login, /{lang}/search, etc.) via
 * multilingualRoutes in routes.js — the Navigation, content fetch, and
 * language detection all work correctly once a language prefix is present.
 *
 * The original component only redirects '/'.  This shadow adds the same
 * redirect for the public non-content routes listed in Volto's
 * NonContentRoutesPublic, so that:
 *
 *   /login              -> /sv/login
 *   /search?q=beds      -> /sv/search?q=beds
 *   /passwordreset/tok  -> /sv/passwordreset/tok
 *
 * Language is determined by the I18N_LANGUAGE cookie (set when the user
 * browses a language-prefixed page) or the site's default language.
 *
 * DR-2026-055.
 */
import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { changeLanguage } from '@plone/volto/actions/language/language';
import { toGettextLang, toBackendLang } from '@plone/volto/helpers/Utils/Utils';

/**
 * Public non-content routes that should be language-prefixed in multilingual
 * mode.  Matches Volto's NonContentRoutesPublic (string entries only).
 *
 * NOT included: CMS-UI routes (/edit, /add, /contents) — those already have
 * language context from the content URL.  NOT included: addon routes
 * (/mobelfakta-dashboard) — those live outside the language tree.
 */
const PUBLIC_NON_CONTENT_ROUTES = [
  '/login',
  '/logout',
  '/sitemap',
  '/register',
  '/search',
  '/change-password',
  '/contact-form',
  '/passwordreset',
];

/**
 * Check if pathname starts with a bare public non-content route.
 * Matches exact ('/login') and prefixed ('/passwordreset/token123').
 */
const isBarePublicNonContentRoute = (pathname) =>
  PUBLIC_NON_CONTENT_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + '/'),
  );

const MultilingualRedirector = (props) => {
  const { pathname, children } = props;
  const location = useLocation();
  const [cookies] = useCookies();
  const site = useSelector((state) => state.site.data);
  const isMultilingual = site.features?.multilingual;
  const availableLanguages = useSelector(
    (state) => state.site?.data?.['plone.available_languages'],
  );
  const currentLanguage = useSelector((state) => state.intl.locale);
  const redirectToLanguage =
    cookies['I18N_LANGUAGE'] || site['plone.default_language'];
  const dispatch = useDispatch();

  React.useEffect(() => {
    let mounted = true;

    const performLanguageSwitch = (targetLang) => {
      const langFileName = toGettextLang(targetLang);
      import(/* @vite-ignore */ '@root/../locales/' + langFileName + '.json')
        .then((locale) => {
          if (mounted) {
            dispatch(changeLanguage(targetLang, locale.default));
          }
        })
        .catch(() => {
          if (mounted) {
            dispatch(changeLanguage(targetLang, {}));
          }
        });
    };

    if (isMultilingual) {
      if (pathname === '/') {
        performLanguageSwitch(redirectToLanguage);
      } else {
        const lang = pathname.split('/')[1];
        if (
          availableLanguages?.includes(lang) &&
          lang !== toBackendLang(currentLanguage)
        ) {
          performLanguageSwitch(lang);
        }
      }
    }
    return () => {
      mounted = false;
    };
  }, [
    pathname,
    dispatch,
    redirectToLanguage,
    isMultilingual,
    availableLanguages,
    currentLanguage,
  ]);

  // --- Redirect logic ---

  if (!isMultilingual) {
    return <>{children}</>;
  }

  // Original: redirect '/' to default language root
  if (pathname === '/') {
    return <Redirect to={`/${toBackendLang(redirectToLanguage)}`} />;
  }

  // New: redirect bare public non-content routes into the language tree
  // Check that the first segment is NOT already a language code
  const firstSegment = pathname.split('/')[1];
  if (
    !availableLanguages?.includes(firstSegment) &&
    isBarePublicNonContentRoute(pathname)
  ) {
    const langPrefix = toBackendLang(redirectToLanguage);
    return (
      <Redirect
        to={{
          pathname: `/${langPrefix}${pathname}`,
          search: location.search,
        }}
      />
    );
  }

  return <>{children}</>;
};

export default MultilingualRedirector;
